/* eslint-disable */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MagnifyingGlass from '../../../Assets/images/svg/dar_magnifying.svg';
import Imagered from '../../../Assets/images/svg/trainingimage.svg';
import AssessmentImg from '../../../Assets/images/svg/assessment.svg';
import UseAxios from '../../../Api/http/http';
import { getTrainingDetails } from '../../../Api/request/training';
import { getAssessementDetails } from '../../../Api/request/assessment';
import { CircularProgress } from '@mui/material';
import './trainingDetails.scss';


const TrainingDetails = ({ userEmailId, userId, snackbarOpen = () => { } }) => {
  const [trainings, setTrainings] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalDuration, setTotalDuration] = useState({
    currentMonth: '0m',
    currentYear: '0m',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);

  const handleResize = () => {
    setIsMobileView(window.innerWidth <= 768);
  };

  useEffect(() => {
    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchData = async (userEmailId, userId, snackbarOpen, setTrainings, setTotalDuration, setLoading, isMounted) => {
    setLoading(true);
    try {
      const [trainingResponse, assessmentResponse] = await Promise.all([
        fetchTrainingDetails(userEmailId, snackbarOpen).catch(() => ({
          trainings: [],
          totalDurationCurrentMonth: '0m 0s',
          totalDurationCurrentYear: '0m 0s',
        })),
        fetchAssessmentDetails(userId, snackbarOpen).catch(() => ({
          trainings: [],
          currentMonthDuration: '0m 0s',
          currentYearDuration: '0m 0s',
        })),
      ]);
      if (isMounted) {
        // Combine training and assessment details
        const combinedTrainings = [
          ...(trainingResponse.trainings || []),
          ...(assessmentResponse.trainings || []),
        ];
        setTrainings(combinedTrainings);
        // Combine total durations
        const totalMonthDuration = addDurations(
          trainingResponse.totalDurationCurrentMonth || '0m 0s',
          assessmentResponse.currentMonthDuration || '0m 0s'
        );
        const totalYearDuration = addDurations(
          trainingResponse.totalDurationCurrentYear || '0m 0s',
          assessmentResponse.currentYearDuration || '0m 0s'
        );

        setTotalDuration({
          currentMonth: totalMonthDuration,
          currentYear: totalYearDuration,
        });
      }
    } catch (error) {
      console.error('Error fetching training and assessment details:', error);
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;

    fetchData(userEmailId, userId, snackbarOpen, setTrainings, setTotalDuration, setLoading, isMounted);

    return () => {
      isMounted = false;
    };
  }, []);

  const fetchTrainingDetails = async (userEmailId, snackbarOpen) => {
    if (!userEmailId) {
      console.error('Email ID is required to fetch training details.');
      return [];
    }

    try {
      const requestConfig = getTrainingDetails(userEmailId);
      const response = await UseAxios(requestConfig);

      if (response.data) {
        const { detailedResults, totalDurationCurrentMonth, totalDurationCurrentYear } = response.data;

        const formattedTrainings = detailedResults.map((training) => ({
          title: training.trainingName,
          type: training.trainingType,
          trainingTime: training.trainingDuration,
          status: training.trainingStatus.charAt(0).toUpperCase() + training.trainingStatus.slice(1),
          details: [
            { label: 'Source', value: training.trainingSource },
            { label: 'Type', value: training.trainingType },
            { label: 'Started on', value: training.trainingStartDate },
            { label: 'Completed on', value: training.trainingCompleteDate || 'N/A' },
          ],
        }));

        return {
          trainings: formattedTrainings,
          totalDurationCurrentMonth,
          totalDurationCurrentYear,
        };
      } else {
        console.error('Unexpected response format:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching training details:', error);
      snackbarOpen('Failed to fetch training details. Please try again.', 'warning');
      return [];
    }
  };

  const fetchAssessmentDetails = async (userId, snackbarOpen) => {
    if (!userId) {
      console.error('User ID is required to fetch assessment details.');
      return [];
    }

    try {
      const requestConfig = getAssessementDetails(userId);
      const response = await UseAxios(requestConfig);

      if (response.data && Array.isArray(response.data.assesementDetails)) {
        const { currentMonthDuration, currentYearDuration, assesementDetails } = response.data;

        const formattedAssessments = assesementDetails.map((assessment) => {
          const lastAttempt = assessment.assessmentReport[assessment.assessmentReport.length - 1];
          const finalStatus = lastAttempt?.status || 'N/A';

          return {
            title: assessment.assessmentName,
            type: 'Assessment',
            trainingTime: assessment.assessmentDuration,
            status: finalStatus === 'Pass' ? 'Cleared' : finalStatus === 'Fail' || finalStatus === 'Below Average' ? 'Not cleared' : finalStatus,
            details: [
              {
                label: 'Attempts',
                value: Array.isArray(assessment.assessmentReport) ? assessment.assessmentReport : [],
              },
              { label: 'Duration', value: assessment.assessmentDuration || 'N/A' },
            ],
          };
        });

        return {
          trainings: formattedAssessments,
          currentMonthDuration,
          currentYearDuration,
        };
      } else {
        console.error('Unexpected response format:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching assessment details:', error);
      snackbarOpen('Failed to fetch assessment details. Please try again.', 'warning');
      return [];
    }
  };

const addDurations = (duration1, duration2) => {
  const parseDuration = (duration) => {
    if (!duration || typeof duration !== 'string') {
      return { hours: 0, minutes: 0, seconds: 0 };
    }
    let hours = 0, minutes = 0, seconds = 0;
    const hourMatch = duration.match(/(\d+)\s*h/);
    const minuteMatch = duration.match(/(\d+)\s*m/);
    const secondMatch = duration.match(/(\d+)\s*s/);
    if (hourMatch) hours = parseInt(hourMatch[1], 10);
    if (minuteMatch) minutes = parseInt(minuteMatch[1], 10);
    if (secondMatch) seconds = parseInt(secondMatch[1], 10);
    return { hours, minutes, seconds };
  };

  const d1 = parseDuration(duration1);
  const d2 = parseDuration(duration2);

  let totalSeconds = d1.seconds + d2.seconds;
  let totalMinutes = d1.minutes + d2.minutes + Math.floor(totalSeconds / 60);
  let totalHours = d1.hours + d2.hours + Math.floor(totalMinutes / 60);

  totalSeconds = totalSeconds % 60;
  totalMinutes = totalMinutes % 60;

  let result = '';
  if (totalHours > 0) {
    result += `${totalHours}h ${totalMinutes}m`;
    // Do not show seconds if there are hours
  } else {
    result += `${totalMinutes}m ${totalSeconds}s`;
  }
  return result.trim();
};
  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const getImage = (type) => {
    return type === 'Assessment' ? AssessmentImg : Imagered;
  };


  if (loading) {
    return (
      <div className="loading-container">
        <CircularProgress size={30} />
      </div>
    );
  }

  const filteredTrainings = trainings.filter((training) =>
    training.title.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const getOrdinal = (n) => {
    const ordinals = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'];
    return ordinals[n] || `${n + 1}th`;
  };
  return (
    <div className="training-outer-layer">
      <div className="training-assessment-container">
        <div className="training-page-heading-div">
          <div className="training-heading-name">Trainings and assessments</div>
          <div className="summary-header">
            <div className="summary-hours-spent">
              <div className="heading">Total hours spent</div>
              <div className="total">This year: {totalDuration.currentYear}</div>
              <div className="total">This month: {totalDuration.currentMonth}</div>
              <div className="separator"></div>
            </div>
            <div className="training-search" style={{ fontWeight: 'normal' }}>
              <img src={MagnifyingGlass} alt="Search" />
              <input
                id="certification-search-input"
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ fontWeight: 'normal' }}
              />
            </div>
          </div>
        </div>

        {filteredTrainings.map((item, index) => (
          <Accordion
            key={item?.title}
            expanded={expanded === index}
            onChange={handleChange(index)}
            className="custom-accordion"
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className={`accordion-summary ${item.type}`}
            >
              <img src={getImage(item.type)}
                alt={item.type}
                className="training-image" />

              <Typography className="training-title">{item.title}</Typography>
              {!isMobileView && (
                <>

                  <Typography
                    className={`training-time ${item.type !== 'Assessment' ? 'adjust-margin' : ''}`}
                  >
                    {item.type === 'Assessment' ? 'Total hours spent' : 'Training Time'}: <span className="training-time-value">{item.trainingTime || 'N/A'}</span>
                  </Typography>
                  <div className="vertical-line"></div>
                  <Typography
                    className={`status ${item.status.replace(' ', '-').toLowerCase()}`}
                  >
                    {item.status}
                  </Typography>
                </>
              )}

            </AccordionSummary>
            {expanded === index && (
              <div className="summary-separator-line"></div>
            )}
            <AccordionDetails>
              {isMobileView && (
                <div className="training-details-mobile">
                  <Typography className="training-time">
                    {item.type === 'Assessment' ? 'Total hours spent' : 'Training Time'}: <span className="training-time-value">{item.trainingTime || 'N/A'}</span>
                  </Typography>

                  <div className="vertical-line"></div>
                  <Typography
                    className={`status ${item.status
                      .replace(' ', '-')
                      .toLowerCase()}`}
                  >
                    {item.status}
                  </Typography>
                </div>
              )}
              {item.type === 'Assessment' ? (
                <div className="ui-custom-container">
                  <div className="attempts-container">
                    {item.details[0]?.value.map((attempt, index) => (
                      <div key={`attempt-${index}`} className="attempt-box">
                        <Typography className="label">{getOrdinal(index)} Attempt</Typography>
                        <Typography
                          className="value"
                          style={{
                            color: attempt.status === 'Pass' ? 'green' : attempt.status === 'Fail' || attempt.status === 'Below Average' ? 'red' : 'black',
                          }}
                        >
                          {attempt.marks}
                        </Typography>
                      </div>
                    ))}
                  </div>
                  <div className="feedback-box">
                    <Typography className="feedback-label">Feedback</Typography>
                    <div className="feedback-blue-line"></div>
                  </div>
                </div>
              ) : (
                <div className="training-details">
                  <div className="detail-section">
                    {item.details
                      .slice(0, Math.ceil(item.details.length / 2))
                      .map((detail) => (
                        <div key={detail?.label} className="detail">
                          <span className="label">{detail.label}</span>
                          <span className="value">: {detail.value}</span>
                        </div>
                      ))}
                  </div>
                  <div className="detail-section">
                    {item.details
                      .slice(Math.ceil(item.details.length / 2))
                      .map((detail) => (
                        <div key={detail?.label} className="detail">
                          <span className="label">{detail.label}</span>
                          <span className="value">: {detail.value}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
    </div>
  );
};

TrainingDetails.propTypes = {
  userEmailId: PropTypes.string.isRequired,
  snackbarOpen: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
};

export default TrainingDetails;

